/**
 * Centralized date/time helper module.
 *
 * This is the ONLY file in the codebase that should import date-fns directly.
 * All other files should import date/time utilities from here.
 *
 * Format constants use moment-style tokens for backwards compatibility.
 * The internal `momentToDateFns` translator converts them at runtime.
 */
import {
  format,
  formatDistanceToNow,
  startOfDay,
  endOfDay,
  addDays,
  addMonths,
  addYears,
  addHours,
  differenceInDays,
  differenceInHours,
  differenceInMinutes,
  differenceInMilliseconds,
  isFuture as dateFnsIsFuture,
  isWithinInterval,
  compareAsc as dateFnsCompareAsc,
  isValid,
  parseISO
} from 'date-fns'

export type DateInput = Date | string | number | null | undefined

// ===========================
// INTERNAL HELPERS
// ===========================

function toNativeDate(date: DateInput): Date | null {
  if (date == null) return null
  if (date instanceof Date) return isValid(date) ? date : null
  if (typeof date === 'number') return new Date(date)
  if (typeof date === 'string') {
    const parsed = parseISO(date)
    if (isValid(parsed)) return parsed
    const fallback = new Date(date)
    if (isValid(fallback)) return fallback
    return null
  }
  return null
}

/**
 * Converts moment.js format tokens to date-fns format tokens.
 * Handles all format constants and inline format strings used in this codebase.
 */
function momentToDateFns(pattern: string): string {
  return pattern
    .replace(/\[([^\]]+)\]/g, "'$1'") // [at] → 'at' (literal text escape)
    .replace(/YYYY/g, 'yyyy') // 4-digit year
    .replace(/YY/g, 'yy') // 2-digit year
    .replace(/Do/g, 'do') // ordinal day (1st, 2nd) — must come before DD/D
    .replace(/DD/g, 'dd') // zero-padded day
    .replace(/\bD\b/g, 'd') // day of month (non-padded)
    .replace(/dddd/g, 'EEEE') // full day name (Monday)
    .replace(/\bA\b/g, '__AMPM_UPPER__') // uppercase AM/PM → placeholder
    .replace(/a(?=[^a-z']|$)/g, '__AMPM_LOWER__') // lowercase am/pm → placeholder
    .replace(/\bLL\b/g, 'MMMM d, yyyy') // locale long date
    .replace(/\bLT\b/g, 'h:mm __AMPM_UPPER__') // locale time (uppercase AM/PM)
    .replace(/__AMPM_UPPER__/g, 'a') // date-fns 'a' = uppercase AM/PM
    .replace(/__AMPM_LOWER__/g, 'aaa') // date-fns 'aaa' = lowercase am/pm
}

// ===========================
// FORMAT CONSTANTS
// ===========================

export const DATE_SHORT = 'MM/DD/YYYY'
export const DATE_TIME_FULL = 'MM/DD/YYYY hh:mm:ss A'
export const DATE_TIME_COMPACT = 'MM/DD h:mma'
export const DATE_MONTH_DAY = 'MM/DD'
export const DATE_LOCALE_LONG = 'LL'
export const DATE_LOCALE_WITH_TIME = 'dddd, LL LT'
export const DATE_COMPACT = 'MMDDYY'
export const DATE_VERBOSE = 'dddd, MMMM D, YYYY'
export const TIME_FULL = 'hh:mm:ss a'
export const DATE_MEDIUM = 'MMM DD, YYYY'
export const DATE_EVENT_LOG = 'MMM Do YYYY [at] h:mm a'
export const DATE_AT_TIME = 'MM/DD [at] HH:mm'
export const TIME_SHORT = 'hh:mm a'
export const DATE_SHORT_YEAR = 'MM/DD/YY'

// ===========================
// FORMATTING
// ===========================

/**
 * Format a date using a native date-fns format string.
 * @see https://date-fns.org/docs/format
 * @example formatDateFns(date, 'MM/dd/yyyy h:mm a')
 */
export function formatDateFns(date: DateInput, pattern: string): string | null {
  if (!date) return null
  const d = toNativeDate(date)
  if (!d) return null
  return format(d, pattern)
}

/**
 * Format a date using a moment-style format constant.
 * @deprecated This uses old moment style date patterns, moment is deprecated. Use {@link formatDateFns} with a native date-fns pattern instead.
 */
export function formatDate(date: DateInput, pattern: string): string | null {
  if (!date) return null
  const d = toNativeDate(date)
  if (!d) return null
  return format(d, momentToDateFns(pattern))
}

// ===========================
// RELATIVE TIME
// ===========================

/** Return a human-relative string like "2 hours ago" */
export function fromNow(date: DateInput): string | null {
  if (!date) return null
  const d = toNativeDate(date)
  if (!d) return null
  return formatDistanceToNow(d, { addSuffix: true })
}

// ===========================
// MANIPULATION
// ===========================

/** Get the start of the local day (00:00:00.000) for a given date */
export function toStartOfDay(date: DateInput): Date {
  const d = toNativeDate(date)
  if (!d) throw new Error('toStartOfDay: invalid date')
  return startOfDay(d)
}

/** Get the end of the local day (23:59:59.999) for a given date */
export function toEndOfDay(date: DateInput): Date {
  const d = toNativeDate(date)
  if (!d) throw new Error('toEndOfDay: invalid date')
  return endOfDay(d)
}

/** Add (or subtract with negative values) time to a date and return a native Date */
export function addTime(date: DateInput, amount: number, unit: 'days' | 'months' | 'years' | 'hours'): Date {
  const d = toNativeDate(date)
  if (!d) throw new Error('addTime: invalid date')
  switch (unit) {
    case 'days':
      return addDays(d, amount)
    case 'months':
      return addMonths(d, amount)
    case 'years':
      return addYears(d, amount)
    case 'hours':
      return addHours(d, amount)
    default:
      throw new Error(`addTime: unknown unit '${unit}'`)
  }
}

// ===========================
// COMPARISON / DIFF
// ===========================

/** Get the difference between two dates in the given unit */
export function diffIn(end: DateInput, start: DateInput, unit: 'days' | 'hours' | 'minutes' | 'milliseconds'): number {
  const e = toNativeDate(end)
  const s = toNativeDate(start)
  if (!e) throw new Error('diffIn: invalid end date')
  if (!s) throw new Error('diffIn: invalid start date')
  switch (unit) {
    case 'days':
      return differenceInDays(e, s)
    case 'hours':
      return differenceInHours(e, s)
    case 'minutes':
      return differenceInMinutes(e, s)
    case 'milliseconds':
      return differenceInMilliseconds(e, s)
    default:
      throw new Error(`diffIn: unknown unit '${unit}'`)
  }
}

/** Check if a date is in the future */
export function isFuture(date: DateInput): boolean {
  if (!date) return false
  const d = toNativeDate(date)
  if (!d) return false
  return dateFnsIsFuture(d)
}

/** Check if a date is between two others */
export function isBetween(date: DateInput, start: DateInput, end: DateInput): boolean {
  if (!date) return false
  const d = toNativeDate(date)
  const s = toNativeDate(start)
  const e = toNativeDate(end)
  if (!d || !s || !e) return false
  return isWithinInterval(d, { start: s, end: e })
}

/** Compare two dates for sorting (ascending). Returns negative if a < b, positive if a > b, 0 if equal. */
export function compareAsc(a: DateInput, b: DateInput): number {
  const dateA = toNativeDate(a)
  const dateB = toNativeDate(b)
  if (!dateA) throw new Error('compareAsc: invalid date a')
  if (!dateB) throw new Error('compareAsc: invalid date b')
  return dateFnsCompareAsc(dateA, dateB)
}

// ===========================
// VALIDATION
// ===========================

/** Check if a date value can be parsed into a valid date */
export function isValidDate(date: DateInput): boolean {
  if (!date) return false
  return toNativeDate(date) !== null
}

// ===========================
// PARSING / CONVERSION
// ===========================

/** Parse a date string or Date to a native Date object. Returns null if invalid. */
export function toDate(date: DateInput): Date | null {
  if (!date) return null
  return toNativeDate(date)
}

/** Convert a date to an ISO 8601 string */
export function toISOString(date: DateInput): string | null {
  if (!date) return null
  const d = toNativeDate(date)
  if (!d) return null
  return d.toISOString()
}

// ===========================
// TIMEZONE
// ===========================

/** Get the local hour equivalent of a specific hour in a given timezone */
export function localHourForTimezoneHour(hour: number, timezone: string): number {
  // Create a date and set it to the target hour in the target timezone using Intl API
  const now = new Date()
  // Get the current hour in the target timezone
  const tzFormatter = new Intl.DateTimeFormat('en-US', {
    timeZone: timezone,
    hour: 'numeric',
    hour12: false
  })
  const tzHourNow = parseInt(tzFormatter.format(now), 10)
  // Offset between target timezone hour and local hour
  const localHourNow = now.getHours()
  const offset = localHourNow - tzHourNow
  // Apply the same offset to the requested hour
  return (((hour + offset) % 24) + 24) % 24
}
